import { useTranslation } from "react-i18next";

import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { ToolAccess } from "@/lib/api";

import {
  type AppGrant,
  type GrantRow,
  missingUnits,
  unitsText,
  withGrant,
  withPartGrant,
} from "./utils";

const ACCESS: readonly ToolAccess[] = ["read", "write"];

interface AccessGridProps {
  rows: readonly GrantRow[];
  /** The apps the team can be given; none for the default team, which the server refuses one. */
  apps?: readonly AppGrant[];
  /** The ticked parts and apps, such as "finance:read" and "app:bezetting". */
  grants: readonly string[];
  onChange: (grants: string[]) => void;
  disabled?: boolean;
}

/**
 * A team's access as a grid: a row per group (its title and summary), a
 * checkbox for Lezen and one for Schrijven. A group gets a box only where it
 * has tools with that access; a part whose tools are all coming soon says so
 * under its box. Under the groups, an Apps block: a row per app with what it
 * requires in muted text. An app's box stays off while the team lacks one of
 * those (the tooltip names which), and unticking such a part drops the app.
 */
export function AccessGrid({
  rows,
  apps = [],
  grants,
  onChange,
  disabled = false,
}: AccessGridProps) {
  const { t } = useTranslation();
  const granted = new Set(grants);
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/10">
            <th className="px-3 py-2 text-left font-medium">{t("teams.editor.access")}</th>
            {ACCESS.map((access) => (
              <th key={access} className="w-24 px-2 py-2 text-center font-medium max-sm:w-20">
                {t(`tools.access.${access}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t">
              <th scope="row" className="px-3 py-1.5 text-left font-normal">
                <span className="text-foreground block font-medium">{row.title}</span>
                <span className="text-muted block text-xs">{row.summary}</span>
              </th>
              {ACCESS.map((access) => {
                const part = row[access];
                return (
                  <td key={access} className="px-2 py-1.5 text-center align-middle">
                    {part && (
                      <span className="inline-flex flex-col items-center gap-0.5">
                        <Checkbox
                          checked={granted.has(part.unit)}
                          onCheckedChange={(on) =>
                            onChange(withPartGrant(grants, part.unit, on === true, apps))
                          }
                          disabled={disabled}
                          aria-label={t("teams.partTitle", {
                            group: row.title,
                            access: t(`tools.access.${access}`),
                          })}
                        />
                        {part.comingSoon && (
                          <span className="text-muted text-xs leading-none">
                            {t("teams.editor.comingSoon")}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {apps.length > 0 && (
          <tbody data-block="apps">
            <tr className="bg-muted/10 border-t">
              <th scope="colgroup" colSpan={3} className="px-3 py-2 text-left font-medium">
                {t("teams.apps.title")}
              </th>
            </tr>
            {apps.map((app) => {
              const checked = granted.has(app.unit);
              const missing = missingUnits(app, granted);
              // A team that lacks a requirement cannot be given the app, but can always lose it.
              const blocked = !checked && missing.length > 0;
              const box = (
                <Checkbox
                  checked={checked}
                  onCheckedChange={(on) => onChange(withGrant(grants, app.unit, on === true))}
                  disabled={disabled || blocked}
                  aria-label={t("teams.apps.grant", { app: app.title })}
                />
              );
              return (
                <tr key={app.unit} className="border-t">
                  <th scope="row" className="px-3 py-1.5 text-left font-normal">
                    <span className="text-foreground block font-medium">{app.title}</span>
                    <span className="text-muted block text-xs">
                      {t("teams.apps.requires", { units: unitsText(app.requires, rows) })}
                    </span>
                  </th>
                  <td colSpan={2} className="px-2 py-1.5 text-center align-middle">
                    {blocked ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {/* A disabled box takes no focus or hover: its holder carries the reason. */}
                          <span
                            tabIndex={0}
                            data-blocked=""
                            className="focus-visible:ring-ring inline-flex rounded-sm focus-visible:ring-2 focus-visible:outline-none"
                          >
                            {box}
                            <span className="sr-only">
                              {t("teams.apps.missing", { units: unitsText(missing, rows) })}
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("teams.apps.missing", { units: unitsText(missing, rows) })}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      box
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </div>
  );
}
